/**
 * SleepPlanSettings
 *
 * Bottom-sheet settings modal for the Sleep Plan.
 * Contains: mode selection, auto-upgrade toggle, quiet hours, premature correction, deactivate.
 * Notification detail settings live in /profile/notifications.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, AlertTriangle, Eye, BookOpen, Zap, Lock } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { SleepPlanSettings as SettingsType } from '../hooks/useSleepPlan';
import type { SleepPlanMode } from '../lib/sleepScoring';

interface SleepPlanSettingsProps {
  settings: SettingsType;
  correctedAgeWeeks?: number;
  onClose: () => void;
  onUpdate: (updates: Partial<SettingsType>) => Promise<void>;
  onChangeMode?: (mode: SleepPlanMode) => Promise<void>;
  onDeactivate: () => Promise<void>;
}

export function SleepPlanSettings({ settings, correctedAgeWeeks = 0, onClose, onUpdate, onChangeMode, onDeactivate }: SleepPlanSettingsProps) {
  const { t } = useTranslation('sleepPlan');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Local form state
  const [localSettings, setLocalSettings] = useState({
    quietHoursStart: settings.quietHoursStart,
    quietHoursEnd: settings.quietHoursEnd,
    autoUpgradeMode: settings.autoUpgradeMode,
  });

  const canSelectLearn = correctedAgeWeeks >= 8;
  const canSelectActive = correctedAgeWeeks >= 12 && settings.totalCyclesWithFeedback >= 10;

  const handleSave = async () => {
    setIsSaving(true);
    await onUpdate(localSettings);
    setIsSaving(false);
    onClose();
  };

  const handleDeactivate = async () => {
    setIsSaving(true);
    await onDeactivate();
    setIsSaving(false);
    onClose();
  };

  const handleModeChange = async (mode: SleepPlanMode) => {
    if (!onChangeMode) return;
    if (mode === 'learn' && !canSelectLearn) return;
    if (mode === 'active' && !canSelectActive) return;
    await onChangeMode(mode);
  };

  return createPortal(
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal" style={{ maxHeight: '85vh' }}>
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{t('settings.title')}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4 overflow-y-auto" style={{ maxHeight: '50vh' }}>
            {/* Mode Selection */}
            {onChangeMode && (
              <div>
                <h3 className="text-white font-medium text-sm mb-2">{t('settings.mode')}</h3>
                <div className="space-y-2">
                  {([
                    { mode: 'observe' as SleepPlanMode, icon: Eye, locked: false },
                    { mode: 'learn' as SleepPlanMode, icon: BookOpen, locked: !canSelectLearn },
                    { mode: 'active' as SleepPlanMode, icon: Zap, locked: !canSelectActive },
                  ]).map(({ mode, icon: Icon, locked }) => (
                    <button
                      key={mode}
                      onClick={() => handleModeChange(mode)}
                      disabled={locked}
                      className={`w-full flex items-center gap-3 p-3 rounded-[10px] transition-colors ${
                        locked ? 'bg-white/5 opacity-50 cursor-not-allowed' :
                        settings.mode === mode
                          ? 'bg-pp-teal/20 border border-pp-teal/50'
                          : 'bg-white/10'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        locked ? 'bg-white/10' : settings.mode === mode ? 'bg-pp-teal/20' : 'bg-white/10'
                      }`}>
                        {locked ? (
                          <Lock className="w-3.5 h-3.5 text-white/20" />
                        ) : (
                          <Icon className={`w-3.5 h-3.5 ${settings.mode === mode ? 'text-pp-teal' : 'text-white/40'}`} />
                        )}
                      </div>
                      <span className={`text-sm font-medium flex-1 text-left ${locked ? 'text-white/30' : settings.mode === mode ? 'text-white' : 'text-white/60'}`}>
                        {t(`mode.${mode}`)}
                      </span>
                      {locked && (
                        <span className="text-white/30 text-xs whitespace-nowrap">{t('onboarding.modeLocked')}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Auto-upgrade toggle */}
            <div className="glass-effect rounded-[15px] p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-3">
                  <span className="text-white text-sm font-medium block">{t('settings.autoUpgrade')}</span>
                  <span className="text-white/50 text-xs">{t('settings.autoUpgradeHint')}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={localSettings.autoUpgradeMode}
                  onClick={() => setLocalSettings(s => ({ ...s, autoUpgradeMode: !s.autoUpgradeMode }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:ring-offset-2 focus:ring-offset-pp-dark ${
                    localSettings.autoUpgradeMode ? 'bg-pp-orange' : 'bg-white/20'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      localSettings.autoUpgradeMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Quiet hours */}
            <div>
              <h3 className="text-white font-medium text-sm mb-2">{t('settings.quietHours')}</h3>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <input
                    type="time"
                    value={localSettings.quietHoursStart}
                    onChange={(e) => setLocalSettings(s => ({ ...s, quietHoursStart: e.target.value }))}
                    className="w-full p-2 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-pp-teal focus:outline-none"
                  />
                </div>
                <span className="text-white/50">-</span>
                <div className="flex-1">
                  <input
                    type="time"
                    value={localSettings.quietHoursEnd}
                    onChange={(e) => setLocalSettings(s => ({ ...s, quietHoursEnd: e.target.value }))}
                    className="w-full p-2 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-pp-teal focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Premature correction (display only) */}
            {settings.prematureWeeks > 0 && (
              <div className="pt-2 border-t border-white/10">
                <h3 className="text-white font-medium text-sm mb-2">{t('settings.prematureCorrection')}</h3>
                <p className="text-white/60 text-sm">
                  {t('settings.prematureWeeks', { weeks: settings.prematureWeeks })}
                </p>
              </div>
            )}

            {/* Deactivate */}
            <div className="pt-4 border-t border-white/10">
              {!showDeactivateConfirm ? (
                <button
                  onClick={() => setShowDeactivateConfirm(true)}
                  className="w-full py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 font-medium text-sm"
                >
                  {t('deactivate')}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">{t('deactivate')}?</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeactivateConfirm(false)}
                      className="flex-1 py-2 rounded-xl bg-white/20 text-white text-sm"
                    >
                      {t('onboarding.back')}
                    </button>
                    <button
                      onClick={handleDeactivate}
                      disabled={isSaving}
                      className="flex-1 py-2 rounded-xl bg-red-500 text-white text-sm disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('deactivate')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
            >
              {t('onboarding.back')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : t('settings.save')}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
